import { useState } from 'react';
import { Maximize2 } from 'lucide-react';
import type { Aporte } from '../../lib/sesion';
import type { PropsVista } from './registro';
import { Ampliada } from './Ampliada';

/**
 * Vista `cuadrantes`: EL FORMATO DEL MANUAL como tablero de proyección
 * (legible a 6 m). El manifiesto declara las regiones del formato (los 4
 * cuadrantes del mapa de empatía, las 2 columnas de espero/temo, los bloques
 * de un lienzo) y cada aporte cae ORDENADO dentro de su región — nunca
 * tarjetas sueltas ni sobrepuestas. Es la vista para conducir la discusión:
 * clic en una tarjeta la lee a sala entera; clic en el título de una región
 * abre ESA región completa, scrolleable.
 *
 * `proyeccion.opciones` del manifiesto:
 *   titulo          título del tablero.
 *   regiones        [{clave, titulo, campo?}] — las casillas del formato.
 *                   Sin `campo`: la región junta los aportes cuyo
 *                   payload[campo_etiqueta] === clave (un aporte → una región).
 *                   Con `campo`: la región junta payload[campo] de cada aporte
 *                   (formatos de wizard: un aporte aporta a varias regiones).
 *   campo_etiqueta  clave del payload que dice a qué región va el aporte.
 *                   SIN `regiones` declaradas, las regiones se derivan de los
 *                   DATOS: una por valor distinto (etapas de un viaje,
 *                   categorías de un card sorting), en orden de llegada.
 *   titulos         {valor: titulo} para rotular regiones derivadas.
 *   campo_texto     clave del texto de la tarjeta (modo etiqueta) — o LISTA
 *                   de claves: la tarjeta muestra cada campo presente con su
 *                   nombre como microetiqueta. Por defecto 'texto'.
 *   columnas        override de columnas; por defecto 2 para 4 regiones
 *                   (tablero 2×2), n para n≤3, 3 para 5+.
 *
 * Los aportes con texto pero sin región reconocible (una nota de voz dicha
 * sin elegir categoría) NO se pierden: caen en una bandeja «Por ubicar» al
 * final, para que la sala los coloque conversando.
 * Regla C7: solo contenido anónimo; el grupo sí (es agregable), jamás autores.
 */

/** Tarjetas visibles por región; el resto queda tras «ver todo». */
const MAX_POR_REGION = 4;

interface RegionDecl {
  clave: string;
  titulo: string;
  campo?: string;
}

interface TarjetaRegion {
  id: string;
  partes: { campo: string; texto: string }[];
  grupo: string | null;
  creado: string;
  /** Número de orden dentro de la región (opciones.ordenar_por), si aplica. */
  orden: number | null;
}

interface RegionLlena {
  decl: RegionDecl;
  tarjetas: TarjetaRegion[];
}

/** Lee las regiones declaradas del manifiesto, tolerante a basura. */
function regionesDe(opciones: Record<string, unknown>): RegionDecl[] {
  if (!Array.isArray(opciones.regiones)) return [];
  return opciones.regiones
    .map((r): RegionDecl | null => {
      if (typeof r !== 'object' || r == null) return null;
      const o = r as Record<string, unknown>;
      if (typeof o.clave !== 'string' || o.clave.length === 0) return null;
      return {
        clave: o.clave,
        titulo: typeof o.titulo === 'string' && o.titulo ? o.titulo : o.clave,
        campo: typeof o.campo === 'string' && o.campo ? o.campo : undefined,
      };
    })
    .filter((r): r is RegionDecl => r != null);
}

/** Texto no vacío del payload en la clave dada, o null. */
function textoDe(aporte: Aporte, clave: string): string | null {
  const v = (aporte.payload ?? {})[clave];
  if (typeof v !== 'string') return null;
  const limpio = v.trim();
  return limpio.length > 0 ? limpio : null;
}

/** Nombre de campo capitalizado para la microetiqueta («pasa» → «Pasa»). */
function capitalizar(nombre: string): string {
  return nombre.charAt(0).toUpperCase() + nombre.slice(1).replace(/_/g, ' ');
}

export function VistaCuadrantes({ aportes, opciones }: PropsVista) {
  const [ampliada, setAmpliada] = useState<TarjetaRegion | null>(null);
  const [regionAbierta, setRegionAbierta] = useState<RegionLlena | null>(null);

  const titulo = typeof opciones.titulo === 'string' ? opciones.titulo : 'Así va el tablero';
  const campoEtiqueta =
    typeof opciones.campo_etiqueta === 'string' ? opciones.campo_etiqueta : null;
  // `campo_texto` acepta una clave o una LISTA (tarjeta con microetiquetas).
  const camposTexto: string[] = Array.isArray(opciones.campo_texto)
    ? opciones.campo_texto.filter((c): c is string => typeof c === 'string')
    : [typeof opciones.campo_texto === 'string' ? opciones.campo_texto : 'texto'];
  // `ordenar_por`: clave numérica que ordena las tarjetas DENTRO de su región
  // (el paso de un proceso); sin ella, lo más reciente primero.
  const ordenarPor = typeof opciones.ordenar_por === 'string' ? opciones.ordenar_por : null;

  let decls = regionesDe(opciones);

  // Regiones DERIVADAS de los datos (etapas de un viaje, categorías de un
  // card sorting): una por valor distinto de campo_etiqueta, en orden de
  // llegada (los aportes vienen del más nuevo al más viejo: se recorre al
  // revés para que el tablero no se reordene con cada aporte nuevo).
  if (decls.length === 0 && campoEtiqueta) {
    const titulos =
      typeof opciones.titulos === 'object' && opciones.titulos != null
        ? (opciones.titulos as Record<string, unknown>)
        : {};
    const vistas = new Set<string>();
    const claves: string[] = [];
    for (let i = aportes.length - 1; i >= 0; i--) {
      const v = (aportes[i].payload ?? {})[campoEtiqueta];
      if (typeof v === 'string' && v.trim() && !vistas.has(v)) {
        vistas.add(v);
        claves.push(v);
      }
    }
    decls = claves.map((clave) => ({
      clave,
      titulo: typeof titulos[clave] === 'string' ? (titulos[clave] as string) : clave,
    }));
  }
  if (decls.length === 0) return null; // sin regiones ni datos: no hay tablero

  /** Partes de tarjeta del modo etiqueta: cada campo declarado presente. */
  const partesDe = (a: Aporte): { campo: string; texto: string }[] =>
    camposTexto
      .map((campo) => {
        const texto = textoDe(a, campo);
        return texto == null ? null : { campo, texto };
      })
      .filter((p): p is NonNullable<typeof p> => p != null);

  // Llenado de regiones, lo más reciente primero dentro de cada una.
  const porRegion = new Map<string, TarjetaRegion[]>(decls.map((d) => [d.clave, []]));
  const porUbicar: TarjetaRegion[] = [];

  for (const a of aportes) {
    const ordenCrudo = ordenarPor != null ? (a.payload ?? {})[ordenarPor] : null;
    const base = {
      id: a.id,
      grupo: a.grupo_nombre ?? null,
      creado: a.created_at,
      orden: typeof ordenCrudo === 'number' && Number.isFinite(ordenCrudo) ? ordenCrudo : null,
    };
    let ubicado = false;
    for (const d of decls) {
      if (d.campo) {
        // Modo wizard: este aporte puede llenar VARIAS regiones, una por campo.
        const texto = textoDe(a, d.campo);
        if (texto != null) {
          porRegion.get(d.clave)!.push({
            ...base,
            id: `${a.id}:${d.campo}`,
            partes: [{ campo: d.campo, texto }],
          });
          ubicado = true;
        }
      }
    }
    if (campoEtiqueta) {
      const partes = partesDe(a);
      if (partes.length > 0) {
        const etiqueta = (a.payload ?? {})[campoEtiqueta];
        const region = typeof etiqueta === 'string' ? porRegion.get(etiqueta) : undefined;
        if (region) {
          region.push({ ...base, partes });
        } else if (!ubicado) {
          porUbicar.push({ ...base, partes });
        }
        ubicado = true;
      }
    }
    if (!ubicado) {
      // Sin etiqueta ni campos del formato: si trae texto genérico, a la bandeja.
      const texto = textoDe(a, 'texto');
      if (texto != null) porUbicar.push({ ...base, partes: [{ campo: 'texto', texto }] });
    }
  }

  // Con `ordenar_por`, manda el número ascendente (los pasos de un proceso);
  // sin él, lo más reciente primero.
  const compara = (x: TarjetaRegion, y: TarjetaRegion) =>
    ordenarPor != null
      ? (x.orden ?? Infinity) - (y.orden ?? Infinity)
      : y.creado.localeCompare(x.creado);
  const regiones: RegionLlena[] = decls.map((decl) => ({
    decl,
    tarjetas: (porRegion.get(decl.clave) ?? []).sort(compara),
  }));
  porUbicar.sort(compara);

  const hayAlgo = porUbicar.length > 0 || regiones.some((r) => r.tarjetas.length > 0);
  if (!hayAlgo) return null; // sin contenido, la Pantalla mantiene su mensaje de espera

  const columnas =
    typeof opciones.columnas === 'number' && opciones.columnas >= 1
      ? Math.floor(opciones.columnas)
      : decls.length <= 3
        ? decls.length
        : decls.length === 4
          ? 2
          : 3;

  return (
    <section className="mt-10 w-full max-w-6xl text-left">
      <h2 className="mb-6 text-center font-sans text-lg uppercase tracking-wide text-content-muted">
        {titulo}
      </h2>

      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: `repeat(${columnas}, minmax(0, 1fr))` }}
      >
        {regiones.map((r) => (
          <Region
            key={r.decl.clave}
            region={r}
            onAmpliarTarjeta={setAmpliada}
            onAbrirRegion={() => setRegionAbierta(r)}
          />
        ))}
      </div>

      {porUbicar.length > 0 && (
        <div className="mt-4">
          <Region
            region={{
              decl: { clave: '__por_ubicar__', titulo: 'Por ubicar' },
              tarjetas: porUbicar,
            }}
            porUbicar
            onAmpliarTarjeta={setAmpliada}
            onAbrirRegion={() =>
              setRegionAbierta({
                decl: { clave: '__por_ubicar__', titulo: 'Por ubicar' },
                tarjetas: porUbicar,
              })
            }
          />
        </div>
      )}

      {/* Una tarjeta, leída a sala entera. */}
      <Ampliada abierta={ampliada != null} onCerrar={() => setAmpliada(null)}>
        {ampliada && (
          <div className="flex flex-col gap-6">
            {ampliada.partes.map((parte) => (
              <p
                key={parte.campo}
                className="font-display text-4xl font-semibold leading-tight text-content"
              >
                {ampliada.partes.length > 1 && (
                  <span className="mr-3 font-sans text-2xl font-semibold uppercase tracking-wide text-content-muted">
                    {capitalizar(parte.campo)}:
                  </span>
                )}
                {parte.texto}
              </p>
            ))}
            {ampliada.grupo != null && (
              <p className="font-sans text-xl uppercase tracking-wide text-content-muted">
                {ampliada.grupo}
              </p>
            )}
          </div>
        )}
      </Ampliada>

      {/* Una región completa, scrolleable: para detenerse a discutirla. */}
      <Ampliada abierta={regionAbierta != null} onCerrar={() => setRegionAbierta(null)}>
        {regionAbierta && (
          <div className="flex flex-col gap-5">
            <p className="font-display text-3xl font-bold uppercase tracking-wide text-cyan">
              {regionAbierta.decl.titulo}
              <span className="ml-3 font-sans text-2xl font-semibold normal-case tabular-nums text-content-muted">
                {regionAbierta.tarjetas.length}
              </span>
            </p>
            <ul className="flex flex-col gap-3">
              {regionAbierta.tarjetas.map((t) => (
                <li
                  key={t.id}
                  className="rounded-lg border border-border bg-surface-base px-5 py-4"
                >
                  {t.orden != null && (
                    <p className="font-sans text-base font-semibold uppercase tracking-wide text-cyan">
                      Paso {t.orden}
                    </p>
                  )}
                  {t.partes.map((parte) => (
                    <p key={parte.campo} className="font-sans text-2xl leading-snug text-content">
                      {t.partes.length > 1 && (
                        <span className="mr-2 font-sans text-base font-semibold uppercase tracking-wide text-content-muted">
                          {capitalizar(parte.campo)}:
                        </span>
                      )}
                      {parte.texto}
                    </p>
                  ))}
                  {t.grupo != null && (
                    <p className="mt-1 font-sans text-base uppercase tracking-wide text-content-muted">
                      {t.grupo}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Ampliada>
    </section>
  );
}

/** Una casilla del formato: título + conteo + tarjetas ordenadas. */
function Region({
  region,
  porUbicar = false,
  onAmpliarTarjeta,
  onAbrirRegion,
}: {
  region: RegionLlena;
  /** Bandeja de lo aún sin región: mismo tablero, borde punteado. */
  porUbicar?: boolean;
  onAmpliarTarjeta: (t: TarjetaRegion) => void;
  onAbrirRegion: () => void;
}) {
  const { decl, tarjetas } = region;
  const visibles = tarjetas.slice(0, MAX_POR_REGION);
  const restantes = tarjetas.length - visibles.length;

  return (
    <section
      className={`flex min-h-44 flex-col rounded-lg border bg-surface-raised p-4 ${
        porUbicar ? 'border-dashed border-border-strong' : 'border-border-strong'
      }`}
    >
      <button
        type="button"
        onClick={onAbrirRegion}
        disabled={tarjetas.length === 0}
        aria-label={`Ver todo «${decl.titulo}»`}
        className="flex items-center justify-between gap-2 rounded-md text-left focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-default"
      >
        <span className="font-display text-xl font-bold uppercase tracking-wide text-cyan">
          {decl.titulo}
          <span className="ml-2 font-sans text-lg font-semibold normal-case tabular-nums text-content-muted">
            {tarjetas.length}
          </span>
        </span>
        {tarjetas.length > 0 && (
          <Maximize2 className="h-5 w-5 shrink-0 text-content-muted" aria-hidden="true" />
        )}
      </button>

      {tarjetas.length === 0 ? (
        <p className="mt-3 font-sans text-lg text-content-muted">Todavía sin aportes</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-2">
          {visibles.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => onAmpliarTarjeta(t)}
                aria-label="Ampliar este aporte"
                className="w-full rounded-md border border-border bg-surface-base px-3 py-2 text-left transition-colors duration-fast hover:border-cyan focus-visible:outline-none focus-visible:shadow-focus"
              >
                {t.orden != null && (
                  <p className="font-sans text-sm font-semibold uppercase tracking-wide text-cyan">
                    Paso {t.orden}
                  </p>
                )}
                {t.partes.map((parte, i) => (
                  <p
                    key={parte.campo}
                    className={`line-clamp-3 font-sans leading-snug ${
                      i === 0 ? 'text-xl text-content' : 'text-base text-content-muted'
                    }`}
                  >
                    {t.partes.length > 1 && (
                      <span className="mr-1.5 font-sans text-sm font-semibold uppercase tracking-wide text-content-muted">
                        {capitalizar(parte.campo)}:
                      </span>
                    )}
                    {parte.texto}
                  </p>
                ))}
                {t.grupo != null && (
                  <p className="mt-0.5 font-sans text-sm uppercase tracking-wide text-content-muted">
                    {t.grupo}
                  </p>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {restantes > 0 && (
        <button
          type="button"
          onClick={onAbrirRegion}
          className="mt-2 self-start rounded-md font-sans text-lg font-semibold text-content-muted transition-colors duration-fast hover:text-content focus-visible:outline-none focus-visible:shadow-focus"
        >
          +{restantes} más
        </button>
      )}
    </section>
  );
}
